import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ContactConnection {
  id: string;
  contact_id: string;
  sender_id: string;
  sender_name: string | null;
  connected_at: string | null;
  source: string | null;
  match_confidence: number | null;
}

/**
 * Bulk-load all sender↔contact LinkedIn connections with sender names resolved.
 *
 * Source: v_sender_contact_connections — the canonical view that unions:
 *   1. heyreach_connections (real-time HR connection-acceptance signals)
 *   2. unified_campaign_contacts.accepted_at (Phase 6A campaign-acceptance fallback)
 *
 * Previous implementation read from `contact_connections` (CSV-import-only, currently
 * 0 rows) and `outreach_senders` (table doesn't exist). That's why ConnectedBadge
 * never rendered. Phase 6A repointed this to v_sender_contact_connections (~212 rows).
 */
export function useContactConnectionIndex() {
  return useQuery({
    queryKey: ["contact-connections-index"],
    queryFn: async () => {
      const { data: conns, error } = await supabase
        .from("v_sender_contact_connections" as any)
        .select("contact_id, team_profile_id, sender_name, connected_at, connection_source");
      if (error) throw error;

      const rows = (conns || []) as unknown as Array<{
        contact_id: string | null;
        team_profile_id: string | null;
        sender_name: string | null;
        connected_at: string | null;
        connection_source: string | null;
      }>;

      // Index by master contact_id; pick the freshest per-contact entry.
      const byContact = new Map<string, ContactConnection>();
      const contactIds = new Set<string>();
      for (const r of rows) {
        if (!r.contact_id || !r.team_profile_id) continue;
        contactIds.add(r.contact_id);
        const existing = byContact.get(r.contact_id);
        if (!existing || (r.connected_at && (!existing.connected_at || r.connected_at > existing.connected_at))) {
          byContact.set(r.contact_id, {
            id: `${r.contact_id}:${r.team_profile_id}`,
            contact_id: r.contact_id,
            sender_id: r.team_profile_id,
            sender_name: r.sender_name,
            connected_at: r.connected_at,
            source: r.connection_source,
            match_confidence: 1.0,
          });
        }
      }
      return { byContact, contactIds };
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Check if a specific contact has a recorded connection.
 * Returns sender name if connected, null otherwise.
 */
export function useIsConnected(contactId: string | null | undefined) {
  const { data } = useContactConnectionIndex();
  if (!data || !contactId) return { connected: false, entry: null as ContactConnection | null };
  const entry = data.byContact.get(contactId) ?? null;
  return { connected: !!entry, entry };
}
